import React, { useEffect, useState } from 'react';
import { Dropdown, Input, Option, OptionGroup, Switch, Textarea } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { Consultorio, Sector, Sede } from '../../../../models/Entities/Turnero';
import { ErrorType } from '../../../../models/HttpError';
import { useOfficesFormStyles } from '../Offices.Styles';
import {
  ConsultorioFormValues,
  buildConsultorioPayload,
  consultorioToFormValues,
  findById,
  getSectorSedeId,
  validateConsultorioForm,
} from '../Offices.Functions';
import { ConsultorioStatusPill, ErrorBanner } from '../OfficesCommon/OfficesCommon.Component';
import { GuideDrawer, GuideDrawerForm } from '../../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../../theme/components/GuideField';

export interface ConsultorioDrawerProps {
  open: boolean;
  consultorio?: Consultorio;
  defaultSectorId?: number | null;
  sedes: Sede[];
  sectores: Sector[];
  consultorios: Consultorio[];
  isSaving: boolean;
  saveError?: ErrorType;
  onSubmit: (payload: Consultorio, id?: number) => void;
  onClose: () => void;
}

const NO_SECTOR = 'none';
const DESCRIPTION_MAX = 500;

const ConsultorioDrawer = ({
  open,
  consultorio,
  defaultSectorId,
  sedes,
  sectores,
  consultorios,
  isSaving,
  saveError,
  onSubmit,
  onClose,
}: ConsultorioDrawerProps) => {
  const { t } = useTranslation();
  const styles = useOfficesFormStyles();
  const editing = consultorio?.id !== undefined;
  const [values, setValues] = useState<ConsultorioFormValues>(consultorioToFormValues(consultorio, defaultSectorId));
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (open) {
      setValues(consultorioToFormValues(consultorio, defaultSectorId));
      setShowErrors(false);
    }
    // Sólo al abrir / cambiar de consultorio (B11).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, consultorio?.id, defaultSectorId]);

  const errors = validateConsultorioForm(values, consultorios, consultorio?.id);
  const sector = findById(sectores, values.sectorId);
  const sectorLabel = sector
    ? `${sector.name || ''} · ${findById(sedes, getSectorSedeId(sector))?.name || ''}`
    : t('Offices.consultorio.noSector');

  const submit = (event?: React.FormEvent) => {
    event?.preventDefault();
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    onSubmit(buildConsultorioPayload(values, consultorio), consultorio?.id);
  };

  return (
    <GuideDrawer
      open={open}
      size="medium"
      title={editing ? t('Offices.consultorio.editTitle') : t('Offices.consultorio.createTitle')}
      onDismiss={onClose}
      preventDismiss={isSaving}
      closeLabel={t('Offices.actions.close')}
      testId="consultorio-drawer"
      secondaryAction={{ label: t('Offices.actions.cancel') }}
      primaryAction={{ label: isSaving ? t('Offices.actions.saving') : t('Offices.actions.save'), onClick: () => submit(), loading: isSaving }}
    >
      <GuideDrawerForm onSubmit={submit}>
        {saveError && <ErrorBanner title={t('Offices.messages.saveError')} error={saveError} testId="consultorio-save-error" />}
        <GuideField
          label={t('Offices.consultorio.name')}
          required
          hint={t('Offices.consultorio.nameHint')}
          error={showErrors && errors.name ? t(errors.name) : undefined}
        >
          <Input
            value={values.name}
            maxLength={100}
            placeholder={t('Offices.consultorio.namePlaceholder')}
            onChange={(_, d) => setValues((v) => ({ ...v, name: d.value }))}
          />
        </GuideField>
        <GuideField label={t('Offices.consultorio.description')} counter={{ value: values.description.length, max: DESCRIPTION_MAX }}>
          <Textarea
            value={values.description}
            maxLength={DESCRIPTION_MAX}
            resize="vertical"
            placeholder={t('Offices.consultorio.descriptionPlaceholder')}
            onChange={(_, d) => setValues((v) => ({ ...v, description: d.value }))}
          />
        </GuideField>
        <GuideFieldGrid data-testid="consultorio-location">
          <GuideField label={t('Offices.consultorio.sector')} hint={t('Offices.consultorio.sectorHint')}>
            <Dropdown
              value={sectorLabel}
              selectedOptions={[values.sectorId !== undefined && values.sectorId !== null ? String(values.sectorId) : NO_SECTOR]}
              onOptionSelect={(_, d) =>
                setValues((v) => ({ ...v, sectorId: !d.optionValue || d.optionValue === NO_SECTOR ? null : Number(d.optionValue) }))
              }
              data-testid="consultorio-sector"
            >
              <Option value={NO_SECTOR} text={String(t('Offices.consultorio.noSector'))}>
                {t('Offices.consultorio.noSector')}
              </Option>
              {sedes.map((sede) => {
                const own = sectores.filter((s) => getSectorSedeId(s) === sede.id);
                if (own.length === 0) return null;
                return (
                  <OptionGroup key={sede.id} label={sede.name || ''}>
                    {own.map((s) => (
                      <Option key={s.id} value={String(s.id)} text={`${s.name || ''} · ${sede.name || ''}`}>
                        {s.name}
                      </Option>
                    ))}
                  </OptionGroup>
                );
              })}
              {sectores
                .filter((s) => !findById(sedes, getSectorSedeId(s)))
                .map((s) => (
                  <Option key={s.id} value={String(s.id)} text={s.name || ''}>
                    {s.name}
                  </Option>
                ))}
            </Dropdown>
          </GuideField>
          <GuideField
            label={t('Offices.consultorio.maxTurnos')}
            required
            hint={t('Offices.consultorio.maxTurnosHint')}
            error={showErrors && errors.maxTurnosSimultaneos ? t(errors.maxTurnosSimultaneos) : undefined}
          >
            <Input
              type="number"
              min={1}
              step={1}
              value={values.maxTurnosSimultaneos}
              onChange={(_, d) => setValues((v) => ({ ...v, maxTurnosSimultaneos: d.value }))}
            />
          </GuideField>
        </GuideFieldGrid>
        {editing && consultorio ? (
          <GuideField label={t('Offices.consultorio.status')} hint={t('Offices.consultorio.statusEditHint')}>
            <div className={styles.readOnlyBox}>
              <ConsultorioStatusPill consultorio={consultorio} />
              <span className={styles.muted}>
                {consultorio.medicoId ? t('Offices.consultorio.doctorAssigned') : t('Offices.consultorio.noDoctor')}
              </span>
            </div>
          </GuideField>
        ) : (
          <GuideField label={t('Offices.consultorio.available')}>
            <Switch
              checked={values.available}
              label={values.available ? t('Offices.common.yes') : t('Offices.common.no')}
              onChange={(_, d) => setValues((v) => ({ ...v, available: d.checked }))}
            />
          </GuideField>
        )}
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default ConsultorioDrawer;
